import { useEffect, useRef, useState, useCallback, type FC, type RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { useIntl } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft.ts';
import Filter from 'dibs-icons/exports/Filter.ts';
import { getAppliedFilterValues } from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers.ts';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
import { SbRespQuickFilterDropdown } from '../SbRespQuickFilterDropdown/SbRespQuickFilterDropdown.tsx';

import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import { useResizeObserver } from 'dibs-react-hooks/exports/useResizeObserver.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';
import { toggleFilterDisplay } from '../../actions/filterActions.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { getAppliedFilter } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { trackFacetClick } from '../../utils/tracking/searchBrowse/filterTracking.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { isBatchAppliedFilter } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

import { sbMessages } from '../sbMessages.tsx';
import {
    QUICK_FILTERS_BY_VERTICAL,
    QUICK_FILTERS_EMPTY_ARRAY,
} from '../SbSharedRefineMenu/sbSharedQuickFiltersConstants.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbRespQuickFilters.module.css';

import { type SbRespQuickFilters_viewer$key } from './__generated__/SbRespQuickFilters_viewer.graphql.ts';
import { type SbRespQuickFilters_itemSearch$key } from './__generated__/SbRespQuickFilters_itemSearch.graphql.ts';
import { type SbRespQuickFilters_user$key } from './__generated__/SbRespQuickFilters_user.graphql.ts';

const GLOBAL_NAV_TOP_BAR_MIN_HEIGHT = 64;
const GLOBAL_NAV_TOP_BAR_MAX_HEIGHT = 84;
const GLOBAL_NAV_BOTTOM_BAR_HEIGHT = 50;

const viewerFragment = graphql`
    fragment SbRespQuickFilters_viewer on Viewer {
        ...SbRespQuickFilterDropdown_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespQuickFilters_itemSearch on ItemSearchQueryConnection {
        topCategoryL1
        ...SbRespQuickFilterDropdown_itemSearch
        appliedFilters {
            name
            canBeDismissed
            values {
                __typename
            }
        }
        filters {
            name
            originalFilterName
        }
    }
`;

const userFragment = graphql`
    fragment SbRespQuickFilters_user on User {
        ...SbRespQuickFilterDropdown_user
    }
`;

type Props = {
    viewer: SbRespQuickFilters_viewer$key;
    itemSearch: SbRespQuickFilters_itemSearch$key;
    user: SbRespQuickFilters_user$key | null | undefined;
    topSentinelRef: RefObject<HTMLDivElement | null>;
};

export const SbRespQuickFilters: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    topSentinelRef,
}) => {
    const dispatch = useDispatch();
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const intl = useIntl();
    const { filters } = itemSearch;
    const vertical = itemSearch.topCategoryL1 || '';
    const isClient = useClientState();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const isLoading = !isClient || refetchInFlight;
    const [activeFilter, setActiveFilter] = useState<string>('');

    const filterNames = hasKey(QUICK_FILTERS_BY_VERTICAL, vertical)
        ? QUICK_FILTERS_BY_VERTICAL[vertical]
        : QUICK_FILTERS_EMPTY_ARRAY;

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const prevRefetchInFlightRef = useRef(refetchInFlight);
    const [isHeaderSticking, setIsHeaderSticking] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScrollability = useCallback(() => {
        const el = scrollContainerRef.current;
        if (el) {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            const newCanScrollLeft = scrollLeft > 1;
            if (newCanScrollLeft !== canScrollLeft) {
                setCanScrollLeft(newCanScrollLeft);
            }
            // Math.floor is needed below, sometimes scrollLeft comes back with .5 precision resulting in a 0.5 difference when we expect 0
            const newCanScrollRight = Math.floor(scrollWidth - scrollLeft - clientWidth) > 1;
            if (newCanScrollRight !== canScrollRight) {
                setCanScrollRight(newCanScrollRight);
            }
        }
    }, [canScrollLeft, canScrollRight]);

    const getTrackingTrigger = useCallback(
        (filterName: string): string | null | undefined => {
            const originalFilterName = getAppliedFilter(filters, filterName)?.originalFilterName;
            return (
                sbRespRefineMenuFiltersMap[filterName]?.title?.defaultMessage || originalFilterName
            );
        },
        [filters]
    );

    const onOpen = useCallback(
        (filterName: string) => {
            const trackingTrigger = getTrackingTrigger(filterName);
            trackFacetClick({
                trackingTrigger,
                isExpanding: activeFilter !== filterName,
                facetType: 'quick filters',
            });
            setActiveFilter(currentActiveFilter =>
                currentActiveFilter === filterName ? '' : filterName
            );
        },
        [activeFilter, getTrackingTrigger, setActiveFilter]
    );
    const onClose = useCallback(() => {
        const activeTrackingTrigger = getTrackingTrigger(activeFilter);
        if (activeTrackingTrigger) {
            trackFacetClick({
                trackingTrigger: activeTrackingTrigger,
                isExpanding: false,
                facetType: 'quick filters',
            });
        }
        setActiveFilter('');
    }, [activeFilter, getTrackingTrigger]);

    const [onResize] = useDebouncedCallback(checkScrollability);
    useResizeObserver(scrollContainerRef, onResize);

    const handleScrollArrowClick = useCallback((direction: 'left' | 'right') => {
        const el = scrollContainerRef.current;
        if (el) {
            const scrollAmount = Math.max(el.clientWidth * 0.25, 200);
            const targetScrollLeft =
                direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;
            el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
        }
    }, []);

    const appliedFilters = (itemSearch.appliedFilters || []).filter(filterFalsy);

    const appliedFiltersCount = appliedFilters.reduce((acc, appliedFilter) => {
        if (appliedFilter?.canBeDismissed) {
            const isBatchApplied =
                (appliedFilter?.name && isBatchAppliedFilter(appliedFilter.name)) || false;
            const values =
                getAppliedFilterValues({
                    appliedFilter,
                    isBatchApplied,
                }) || [];
            return acc + values.length;
        }
        return acc;
    }, 0);

    useVisibilityTracker({
        elementRef: topSentinelRef,
        options: { rootMargin: `${-1 - GLOBAL_NAV_TOP_BAR_MIN_HEIGHT}px 0px 0px 0px` },
        onVisibilityChange: ({ intersectionRatio }) => {
            setIsHeaderSticking(intersectionRatio < 1);
        },
        watchAfterFirstVisible: true,
    });

    useEffect(() => {
        const prevRefetchInFlight = prevRefetchInFlightRef.current;
        prevRefetchInFlightRef.current = refetchInFlight;

        if (prevRefetchInFlight && !refetchInFlight && topSentinelRef.current && isHeaderSticking) {
            const { top } = topSentinelRef.current.getBoundingClientRect();
            window.scrollTo(
                window.scrollX,
                window.scrollY +
                    top -
                    GLOBAL_NAV_TOP_BAR_MAX_HEIGHT -
                    GLOBAL_NAV_BOTTOM_BAR_HEIGHT -
                    1
            );
        }
    }, [isHeaderSticking, refetchInFlight, topSentinelRef]);

    return (
        <>
            <div
                className={classnames(dibsCss.relative, dibsCss.overflowHidden, dibsCss.flexGrow, {
                    [dibsCss.zMiddle]: activeFilter,
                })}
            >
                <div
                    ref={scrollContainerRef}
                    onScroll={checkScrollability}
                    className={classnames(
                        styles.hideScrollbar,
                        dibsCss.overflowScroll,
                        dibsCss.flex,
                        dibsCss.flexGrow,
                        dibsCss.itemsCenter,
                        dibsCss.gapXxsmall
                    )}
                >
                    {filterNames.map(filterName => (
                        <SbRespQuickFilterDropdown
                            key={filterName}
                            viewer={viewer}
                            itemSearch={itemSearch}
                            user={user}
                            activeFilter={activeFilter}
                            isLoading={isLoading}
                            filterName={filterName}
                            onClose={onClose}
                            onOpen={onOpen}
                        />
                    ))}
                </div>
                <Link
                    className={classnames(
                        styles.arrow,
                        styles.arrowLeft,
                        dibsCss.absolute,
                        dibsCss.left0,
                        dibsCss.top0,
                        {
                            [styles.isHidden]: !canScrollLeft,
                        }
                    )}
                    onClick={() => handleScrollArrowClick('left')}
                >
                    <ChevronLeft variation="20" />
                </Link>
                <Link
                    className={classnames(
                        styles.arrow,
                        styles.arrowRight,
                        dibsCss.absolute,
                        dibsCss.right0,
                        dibsCss.top0,
                        {
                            [styles.isHidden]: !canScrollRight,
                        }
                    )}
                    onClick={() => handleScrollArrowClick('right')}
                >
                    <ChevronRight variation="20" />
                </Link>
            </div>
            <Button
                style="circle"
                size="small"
                type="primaryAlt"
                className={classnames(styles.button, dibsCss.itemsCenter, dibsCss.gapXxsmall)}
                onClick={e => {
                    e.currentTarget.blur();
                    trackEvent(
                        {
                            category: 'results page facet selected',
                            action: 'refine link clicked',
                            eventName: eventNameConstants.EVENT_RESULTS_PAGE_FACET_SELECTED,
                            interaction_type:
                                interactionTypeConstants.INTERACTION_TYPE_FILTER_RESULTS_LINK_CLICKED,
                            step_interaction_name: 'show filters',
                        },
                        e
                    );
                    dispatch(toggleFilterDisplay());
                }}
                disabled={isLoading}
            >
                <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.gapXxsmall)}>
                    <Filter variation="20" />
                    {intl.formatMessage(sbMessages.showAllFilters)}
                    {appliedFiltersCount > 0 && <span>({appliedFiltersCount})</span>}
                </div>
            </Button>
            <div
                className={classnames(
                    styles.shadowLine,
                    dibsCss.sassyBoxShadowDark,
                    dibsCss.absolute,
                    dibsCss.top0,
                    dibsCss.left50,
                    dibsCss.wScreen,
                    dibsCss.hFull,
                    { [dibsCss.hidden]: !isHeaderSticking }
                )}
            />
        </>
    );
};
